import React from 'react';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Image,
  Spinner,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import useConsumeBenefitController from './ConsumeBenefitController';
import './styles.css';
import {
  BikletaPointIcon,
  GiftIcon,
  UserIcon,
  BikeIcon,
  BikletaIcon,
} from '../../components/Icons/Icons';
import { BgBottomConsumeBenefit } from '../../assets';

// Variantes de animación para Framer Motion
const successOverlayVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
      staggerChildren: 0.15,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.4,
    },
  },
};

const successElementVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

const confettiVariants = {
  hidden: {
    opacity: 0,
    scale: 0.5,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: 'backOut',
      delay: 0.2,
    },
  },
};

const MotionBox = motion(Box);
const MotionText = motion(Text);
const MotionVStack = motion(VStack);

const ConsumeBenefitView = () => {
  const {
    couponData,
    isLoading,
    error,
    codeError,
    handleConsumeBenefit,
    handleBack,
    renewalFrequency,
    goToHome,
    canConsume,
    isConsumed,
    benefitDescription,
  } = useConsumeBenefitController();

  if (isLoading) {
    return (
      <Box
        w="100%"
        maxW={{ base: '100%', md: '500px' }}
        mx="auto"
        height="94vh"
        p={4}
        bg="#FDFDFC"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Spinner thickness="4px" speed="0.65s" color="#FFC700" size="xl" />
      </Box>
    );
  }

  if (error && codeError === '') {
    return (
      <Box
        w="100%"
        maxW={{ base: '100%', md: '500px' }}
        mx="auto"
        height="94vh"
        p={4}
        bg="#FDFDFC"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center">
        <Text color="red.500" textAlign="center" mb={4}>
          {error}
        </Text>
        <Box w={'80%'} mt={4}>
          <Button onClick={goToHome} className="backButton">
            Volver
          </Button>
        </Box>
      </Box>
    );
  }

  if (!couponData) {
    return (
      <Box
        w="100%"
        maxW={{ base: '100%', md: '500px' }}
        mx="auto"
        height="94vh"
        p={4}
        bg="#FDFDFC"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center">
        <Text textAlign="center" mb={4}>
          No se encontró información del beneficio
        </Text>
        <Button onClick={() => {}} className="consumeButton">
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <MotionBox
      w="100%"
      maxW={{ base: '100%', md: '500px' }}
      mx="auto"
      height="94vh"
      bg="#FDFDFC"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}>
      {/* Benefit Image */}
      <MotionBox
        position="relative"
        borderBottomRadius="24px"
        overflow="hidden"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}>
        <Image
          src={
            couponData?.benefit?.imageUrl ||
            'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=200&fit=crop'
          }
          alt="Benefit"
          w="100%"
          aspectRatio="16/9"
          objectFit="cover"
        />
        {/* Tag */}
        <MotionBox
          position="absolute"
          top={6}
          right={0}
          bg="#FFD84E"
          color="#0D0C0C"
          px={3}
          py={2}
          borderLeftRadius="8px"
          fontSize="lg"
          fontWeight="600"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}>
          {couponData.benefit.benefitTag}
        </MotionBox>
      </MotionBox>
      {/* Content */}
      <MotionVStack
        spacing={12}
        flex={1}
        alignItems="stretch"
        p={6}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}>
        {/* Success Message */}
        <VStack spacing={3} textAlign="center">
          <Text
            fontWeight="600"
            color="#0D0C0C"
            whiteSpace="nowrap"
            maxW="100%"
            sx={{
              fontSize: 'clamp(18px, 7vw, 24px)',
            }}>
            {codeError !== '' && error ? error : '🎉 ¡Beneficio encontrado!'}
          </Text>
          {codeError !== '' && error ? (
            <Box bg={codeError === 'expired' ? "#F5F3EF" : "#D8D7E2"} px={3} py={1} borderRadius="8px">
              <Text fontSize="sm" color={codeError === 'expired' ? "#A8A6A0" : "#4D4B62"}>
                {codeError === 'stock_reached'
                  ? 'Ya no hay más stock de este beneficio'
                  : codeError === 'expired'
                    ? `Desde el ${new Date(couponData.benefit.startDate).toLocaleDateString()} al ${new Date(couponData.benefit.endDate).toLocaleDateString()}`
                    : `Volverá a habilitarse ${renewalFrequency === '24_hours' ? 'en 24 horas' : 'pronto'}`}
              </Text>
            </Box>
          ) : (
            <Box bg="#C0EDD0" px={3} py={1} borderRadius="8px">
              <Text fontSize="sm" color="#1A8C44">
                Desde el{' '}
                {new Date(couponData.benefit.startDate).toLocaleDateString()} al{' '}
                {new Date(couponData.benefit.endDate).toLocaleDateString()}
              </Text>
            </Box>
          )}
        </VStack>

        {/* Benefit Details */}
        <VStack
          spacing={4}
          p={6}
          borderRadius="24px"
          border={'1px solid #D8D7E2'}>
          {/* Commerce */}
          <HStack w="100%" alignItems="center" spacing={3}>
            <Box
              w="40px"
              h="40px"
              bg="#F3F4F6"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <BikletaPointIcon fontSize="26px" />
            </Box>
            <VStack alignItems="flex-start" spacing={0} flex={1}>
              <Text fontSize="sm" color="#6B7280">
                Comercio:
              </Text>
              <Text fontSize="16px" fontWeight="500" color="#0D0C0C">
                {couponData.point.name}
              </Text>
            </VStack>
          </HStack>

          {/* User */}
          <HStack w="100%" alignItems="center" spacing={3}>
            <Box
              w="40px"
              h="40px"
              bg="#F3F4F6"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <UserIcon fontSize="26px" />
            </Box>
            <VStack alignItems="flex-start" spacing={0} flex={1}>
              <Text fontSize="sm" color="#6B7280">
                Usuario:
              </Text>
              <Text fontSize="16px" fontWeight="500" color="#0D0C0C">
                {couponData.coupon.userName}
              </Text>
            </VStack>
          </HStack>

          {/* Benefit */}
          <HStack w="100%" alignItems="center" spacing={3}>
            <Box
              w="40px"
              h="40px"
              bg="#F3F4F6"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <GiftIcon fontSize="26px" />
            </Box>
            <VStack alignItems="flex-start" spacing={0} flex={1}>
              <Text fontSize="sm" color="#6B7280">
                Beneficio:
              </Text>
              <Text fontSize="16px" fontWeight="500" color="#0D0C0C">
                {benefitDescription}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </MotionVStack>

      {/* Consume Button */}
      <MotionBox
        marginX={14}
        marginBottom={8}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.5 }}>
        <Button
          className="consumeButton"
          bg={canConsume ? '#FFC700' : '#1d1b2034'}
          color="#0D0C0C"
          isDisabled={!canConsume}
          onClick={handleConsumeBenefit}
          _focus={{
            bg: canConsume ? '#FFC700' : '#1d1b2034',
          }}>
          Consumir este beneficio
        </Button>
      </MotionBox>

      {/* Success Animation Overlay */}
      <AnimatePresence>
        {isConsumed && (
          <MotionBox
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="white"
            zIndex={1000}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            variants={successOverlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit">
            {/* Main Content */}
            <MotionVStack
              spacing={8}
              flex={1}
              alignItems="center"
              justifyContent="center"
              px={8}
              py={12}
              variants={successElementVariants}>
              {/* Success Checkmark */}
              <MotionBox
                w="60px"
                h="60px"
                bg="#22C55E"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                variants={confettiVariants}>
                <Text fontSize="30px" color="white" fontWeight="bold">
                  ✓
                </Text>
              </MotionBox>

              {/* Success Title */}
              <MotionText
                fontSize="28px"
                fontWeight="600"
                color="#0D0C0C"
                textAlign="center"
                variants={successElementVariants}>
                Beneficio consumido
              </MotionText>

              {/* Success Message */}
              <MotionText
                fontSize="16px"
                color="#0D0C0C"
                textAlign="center"
                variants={successElementVariants}>
                ¡Gracias por ser un Bikleta Point! 🤗
              </MotionText>

              {/* Bike Icon */}
              <MotionBox mt={4} variants={successElementVariants}>
                <BikletaIcon w="120px" h="120px" color="#0D0C0C" />
              </MotionBox>
            </MotionVStack>

            {/* Bottom Background */}
            <MotionBox
              position="relative"
              w="100%"
              h="160px"
              backgroundImage={BgBottomConsumeBenefit}
              backgroundSize="cover"
              backgroundPosition="bottom"
              backgroundRepeat="no-repeat"
              display="flex"
              alignItems="flex-end"
              justifyContent="center"
              pb={8}
              variants={successElementVariants}>
              <Text fontSize="14px" color="#0D0C0C" fontWeight="500">
                Powered by Bikleta®
              </Text>
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
};

export default ConsumeBenefitView;
